// VendorPage.js
import React, { useEffect, useState, useCallback } from "react";
import { fetchAuthSession } from "@aws-amplify/auth";

const API_BASE_URL = "https://api.cupvisor.org";

function VendorPage({ user, signOut }) {
  const [jwtToken, setJwtToken] = useState("");
  const [description, setDescription] = useState("");
  const [pointsThreshold, setPointsThreshold] = useState("");
  const [creationResponse, setCreationResponse] = useState(null);
  const [vendorCards, setVendorCards] = useState([]);

  useEffect(() => {
    async function loadSession() {
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        setJwtToken(idToken);
      } catch (err) {
        console.error("Error loading session:", err);
      }
    }
    loadSession();
  }, [user]);

  const fetchVendorCards = useCallback(async () => {
    if (!jwtToken) return;
    try {
      const res = await fetch(`${API_BASE_URL}/vendor/cards`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          Authorization: `Bearer ${jwtToken}`,
        },
      });
      if (!res.ok) {
        throw new Error(`API returned status ${res.status}`);
      }
      const data = await res.json();
      setVendorCards(data);
    } catch (err) {
      console.error("Error fetching vendor card types:", err);
    }
  }, [jwtToken]);

  useEffect(() => {
    if (jwtToken) {
      fetchVendorCards();
    }
  }, [jwtToken, fetchVendorCards]);

  const createCardType = async (e) => {
    e.preventDefault();
    if (!jwtToken) {
      console.error("JWT token is not available");
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/vendor/create-card-type`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          Authorization: `Bearer ${jwtToken}`,
        },
        body: JSON.stringify({
          description: description,
          points_threshold: pointsThreshold,
        }),
      });
      if (!res.ok) {
        throw new Error(`API returned status ${res.status}`);
      }
      const data = await res.json();
      setCreationResponse(data);
      setDescription("");
      setPointsThreshold("");
      fetchVendorCards();
    } catch (err) {
      console.error("Error creating card type:", err);
      setCreationResponse({ error: err.message });
    }
  };

  return (
    <div className="mb-5">
      <div className="mb-4">
        <h2 className="h4">Create a New Card Type</h2>
        <form onSubmit={createCardType} className="form-group">
          <label htmlFor="description">Description:</label>
          <input
            id="description"
            type="text"
            className="form-control"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter card type description"
            required
          />
          <label htmlFor="pointsThreshold">Points Threshold:</label>
          <input
            id="pointsThreshold"
            type="number"
            className="form-control"
            value={pointsThreshold}
            onChange={(e) => setPointsThreshold(e.target.value)}
            placeholder="Enter points required"
            required
          />
          <button type="submit" className="btn btn-primary mt-3">
            Create Card Type
          </button>
        </form>
        {creationResponse && (
          <div className="alert alert-info mt-3">
            <pre>{JSON.stringify(creationResponse, null, 2)}</pre>
          </div>
        )}
      </div>

      <div>
        <h2 className="h4">Your Card Types</h2>
        {vendorCards && vendorCards.length > 0 ? (
          <ul className="list-group">
            {vendorCards.map((card, index) => (
              <li key={index} className="list-group-item">
                <strong>{card.description}</strong> (Threshold: {card.default_max_points}){" "}
                <a
                  href={card.join_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-link"
                >
                  Join URL
                </a>
              </li>
            ))}
          </ul>
        ) : (
          <p>No card types to display yet.</p>
        )}
      </div>
    </div>
  );
}

export default VendorPage;
